import type { ReactElement } from 'react';
import { ArrowRight } from 'lucide-react';

interface StatTileProps {
  label: string;
  value: string;
  /** A label of two to four words under the number. Never a sentence. */
  helper?: string;
  /** The longer explanation, when there is one: the tile's hover text, not on the page. */
  hint?: string;
  /** 'warn' (amber) is for a number that is waiting on someone. */
  valueTone?: 'default' | 'good' | 'bad' | 'warn';
  /** Classes for the helper line, when it carries a colour of its own. */
  helperClassName?: string;
  /** Placement in the strip's grid (a column span), never a restyle. */
  className?: string;
  /** 'compact': a shorter tile, for a strip that sits above a long table. */
  size?: 'regular' | 'compact';
  /**
   * Opens the list behind the number. Give one ONLY when a list exists that equals the
   * number and the number is above zero; a ratio, an average or a zero stays a plain tile.
   */
  onClick?: () => void;
}

// The white card every block of the platform is drawn as (the home screen's cards, the
// section pages' cards): the same corner, border and shadow, so a dashboard's tiles read
// as part of the same thing.
const TILE = 'rounded-2xl border border-gray-200 bg-white shadow-sm';
const PAD = { regular: 'p-5', compact: 'px-4 py-3' } as const;
const NUMBER = { regular: 'pt-2 text-3xl', compact: 'pt-1 text-2xl' } as const;
// A tile that opens a list is pressed like any other card: it lifts, and shows the ring.
const TILE_PRESSABLE = 'transition hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

/**
 * A headline number on a dashboard: a small grey label, the number large, and a few grey
 * words under it. With `onClick` it is a button that opens the list behind it (an arrow at
 * the top right says so); without one it is a plain tile. Lifted from the Leave &
 * Attendance Requests dashboard (ApprovalsTab), where every count already opened its
 * list, so the others can do the same with one piece.
 */
export default function StatTile({ label, value, helper, hint, valueTone = 'default', helperClassName = 'text-gray-500', className = '', size = 'regular', onClick }: StatTileProps): ReactElement {
  const cls = valueTone === 'good' ? 'text-emerald-600' : valueTone === 'bad' ? 'text-rose-600' : valueTone === 'warn' ? 'text-amber-600' : 'text-gray-900';
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        {/* mt-0.5: level with the label's first line, which is the one it sits beside
            when a long label wraps. */}
        {onClick && <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600" aria-hidden />}
      </div>
      {/* mt-auto: where a label beside it runs to a second line, the numbers of the
          strip still sit on one line (the tile is a column, as tall as its row). */}
      <div className={`mt-auto break-words font-bold tabular-nums tracking-tight ${NUMBER[size]} ${cls}`}>{value}</div>
      {helper && <div className={`mt-1 text-xs ${helperClassName}`}>{helper}</div>}
    </>
  );
  if (!onClick) return <div title={hint} className={`flex flex-col ${TILE} ${PAD[size]} ${className}`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      // A button centres what it holds; flex-col keeps the label at the top, level with
      // the plain tiles beside it when a label in the strip wraps.
      className={`group flex flex-col text-left ${TILE} ${PAD[size]} ${TILE_PRESSABLE} ${className}`}
    >
      {body}
    </button>
  );
}
